/* eslint-disable no-console */
/**
 * @description   Globaler Service als Abstraktionsschicht der Authentifizierung.
 *                Verwendet BackendService zum Senden von HTTP-Requests.
 *          Token wird über TokenStorageService verwaltet.
 * @author  Massimo Feth <m.feth@pharmakon.software>
 */

// Angular-Module
import {Injectable} from '@angular/core';
import {Router, ActivatedRoute} from '@angular/router';
// ReactiveX for JavaScript
import {Observable, Subject} from 'rxjs';
import {tap, takeUntil} from 'rxjs/operators';
// Service für Übersetzungen über NGX-Translate
import {TranslateService} from '@ngx-translate/core';
// Globale Services
import {BackendService} from './backend.service';
import {InitService} from './init.service';
import {TokenStorageService} from './token-storage.service';
import {UserPermissionsService} from './user-permissions.service';
import {UserSettingsService} from './user-settings.service';
// Moment.js
import {hasOwn} from '@shared/utils';
import moment from 'moment';
import {TitleService} from './title.service';
import {StorageService} from './storage.service';
import {PhoneCallService} from './phone-call.service';
import {EntityRouteService} from './entity-route.service';

@Injectable({
    // Root-Injector (app.module.ts) ist verantwortlich für das Instanziieren dieses globalen Services
    providedIn: 'root',
})
export class AuthService {
    // Wird bei ngOnDestroy ausgelöst um Observables-Subscription zu stoppen
    #componentDestroyed$ = new Subject<void>();

    // Aktuelle Sprache
    languageKey = '';

    // URL speichern um nach Login weiterleiten zu können
    redirectUrl: string;
    get isLoggedIn(): boolean {
        // Token und TokenExpiresAt vorhanden? Token noch gültig?
        if (this.tokenStorageService.token && this.tokenStorageService.refreshToken) {
            // User ist eingeloggt
            return true;
        }

        // Standard (Nicht eingeloggt, kein gültiges oder abgelaufenes Token)
        return false;
    }

    // Konstruktor (inkl. dependency injection)
    constructor(
        private router: Router,
        private translate: TranslateService,
        private backendService: BackendService,
        private initService: InitService,
        private tokenStorageService: TokenStorageService,
        private userPermissionsService: UserPermissionsService,
        private userSettingsService: UserSettingsService,
        private route: ActivatedRoute,
        private titleService: TitleService,
        private storageService: StorageService,
        private phoneCallService: PhoneCallService,
        private entityRouteService: EntityRouteService,
    ) {
        this.languageKey = this.translate.currentLang;

        if (this.isLoggedIn) {
            /*
             * SSE-Stream starten
             * Es wird hier nochmal ausgeführt, damit es auch aktiv ist, wenn die Seite neu geladen wird
             */
            this.phoneCallService.startSseStream();
        }
    }

    /**
     * @param {object} credentials - Benutzername und Passwort
     * @description   Login des Benutzers
     *  Erhält credentials und sendet diese über BackendService als POST-Request.
     *  Reagiert selbst auf das Ergebnis des POST-Requests, leitet
     *  diesen aber auch zur Auswertung an die aufrufende Komponente weiter.
     * @returns {Observable<any>} - Observable mit Ergebnis des POST-Requests
     * @author  Massimo Feth <m.feth@pharmakon.software>
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    login(credentials: object): Observable<any> {
        // POST-Request mit Login-Credentials über BackendService senden
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const postRequest$: Observable<any> = this.backendService.postRequest('users/login', credentials);
        // AuthService subscribed den definierten Post-Request, wodurch dieser ausgelöst wird.
        postRequest$.subscribe(
            // onNext
            (result) => {
                // Login erfolgreich?
                if (result['success']) {
                    // LOGIN ERFOLGREICH
                    this.onSuccessfulLogin(result);
                }
            },
            // onError
            (error) => {
                // Keine Antwort vom Backend erhalten (Servererror)
                console.error('AuthService#login.subscription - Server-Error!');
                console.error(error);
            },
        );
        // Observable (an Komponente) zurücklieferen
        return postRequest$;
    }

    /**
     * @param {any} code - ?
     * @description Weiterreichen des Token ans Backend um zu prüfen ob Microsoft es ausgestellt hat und welche Email
     * @author  Nick Mühlberger <n.muehlberger@pharmakon.software> Eric Haeussel <e.haeusel@pharmakon.software>
     * @returns {Observable<any>} - Observable mit Ergebnis des POST-Requests
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ssoLogin(code: any): Observable<any> {
        const codeObject = {code};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const authServiceLogin$: Observable<any> = this.backendService.postRequest('users/loginSSO', codeObject);
        authServiceLogin$.subscribe(
            // onNext
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (result: any) => {
                // Login fehlgeschlagen?
                if (result.success) {
                    this.onSuccessfulLogin(result, true);
                } else {
                    // Zur Loginseite wechseln
                    this.router.navigate(['/login']);
                }
            },
            // onError
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (error: any) => {
                // Keine Antwort vom Backend erhalten (Servererror)
                console.error('AuthService#ssoLogin.subscription - Server-Error!');
                console.error(error);
            },
        );
        return authServiceLogin$;
    }

    /**
     * @param {any} result - Ergebnis des Login-Requests
     * @param {boolean} skipRedirect - Soll nach Login weitergeleitet werden?
     * @description   Initialisierungen nach erfolgreichem Login
     * @author  Massimo Feth <m.feth@pharmakon.software>
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccessfulLogin(result: any, skipRedirect: boolean = false) {
        // Authentifizierungs-Token setzen
        this.tokenStorageService.token = result['data']['token']['tokenId'];
        this.tokenStorageService.refreshToken = result['data']['refreshToken']['tokenId'];
        this.tokenStorageService.username = result['data']['userData']['username'];

        // Benutzer-Einstellungen speichern (über userSettingsService)
        this.userSettingsService.storeManySettings(result['data']['userSettings']);
        // Benutzer-Berechtigungen in AppCore ablegen
        this.userPermissionsService.storeManyPermissions(result['data']['userPermissions']);

        // Sprache setzen
        if (hasOwn(result['data']['userData'], 'language_key') && result['data']['userData']['language_key'] !== null) {
            // Sprache des Benutzers einstellen --> NGX-Translate
            this.translate.use(result['data']['userData']['language_key']);
            // Sprache des Benutzers einstellen --> moment.js
            moment.locale(result['data']['userData']['language_key'].substring(0, 2));
        } else {
            this.translate.use('deu');
            moment.locale('de');
        }

        // ownUser sofort cachen damit checkLanguage() in layout-main die korrekte Sprache liest
        this.storageService.setItem('ownUser', result['data']['userData']);

        // Initialisierungs-Service aufrufen (zum asynchronen Laden der Core-Daten)
        this.initService.initializeAll();
        this.redirectOnSuccess(skipRedirect);

        // Seiten-Titel setzen
        this.titleService.setWindowTitle();

        // SSE-Stream für Anrufe starten
        this.phoneCallService.startSseStream();
    }

    // Im Erfolgsfall ins system redirecten
    public redirectOnSuccess(skipRedirect: boolean) {
        // Handoff login: keine automatische Weiterleitung, Ziel steuert die aufrufende Komponente
        if (skipRedirect) {
            return;
        }

        const redirect = this.route.snapshot.queryParams['redirect'];

        // ... und nach Prüfung der Berechtigung...
        const presetInitModule: string = this.userPermissionsService.getPermissionValue('presetInitModule');
        /*
         * PDEV-1796: navigateByUrl statt navigate([url]) — redirect und lastModule koennen einen
         * Query-String enthalten (z.B. "/institutions-overview?filter=..."), den navigate() als Teil
         * des Pfads kodiert ("%3Ffilter%3D...") und damit auf "Seite nicht gefunden" fuehrt
         * (gleiches Vorgehen wie in refresh(), PDEV-1767).
         */
        if (redirect) {
            this.router.navigateByUrl(redirect);
        } else if (presetInitModule && presetInitModule.length > 0) {
            // ...wird auf "presetInitModule" weitergeleitet
            this.router.navigate([presetInitModule.toLowerCase()]);
        } else {
            // ...wird auf "lastModule" weitergeleitet
            // lastModule ist die komplette URL inkl. Query-String (global-menu speichert route.url),
            // daher wie oben navigateByUrl statt navigate([lastModule]). Ohne lastModule (neuer
            // User) dasselbe Standardziel wie AuthGuardService (alte oder neue E-Liste, PDEV-1796).
            const lastModule: string = this.userSettingsService.getValue('lastModule');
            if (lastModule) {
                this.router.navigateByUrl(lastModule);
            } else {
                void this.entityRouteService.navigateToList('institutions');
            }
        }
    }

    /**
     * @description Logout des Benutzers
     *  Leert Local-Storage (und dadurch auch JWT)
     * @author  Massimo Feth <m.feth@pharmakon.software>
     */
    logout(): void {
        this.tokenStorageService.token = null;
        this.tokenStorageService.refreshToken = null;
        this.tokenStorageService.username = null;

        if (this.tokenStorageService.isImpersonationTab) {
            /*
             * Impersonate-Tab: nur den Tab-lokalen Session-Storage leeren, damit
             * die Admin-Session im ursprünglichen Tab (Local-Storage) erhalten bleibt.
             */
            window.sessionStorage.clear();
        } else {
            // LocalStorage komplett leeren
            localStorage.clear();
            // Session-Storage komplett leeren
            window.sessionStorage.clear();
            window.localStorage.clear();
        }

        // Seiten-Titel zurücksetzen
        this.titleService.resetWindowTitle();

        // TODO: generell alle sses beenden
        this.phoneCallService.stopSseStream();
    }

    refresh() {
        if (!this.isLoggedIn) return;

        this.initService.initialized = false;
        const authServiceLogin$: Observable<any> = this.login({});
        authServiceLogin$.subscribe(
            // onNext
            (result: any) => {
                const currentUrl = this.router.url;
                this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
                    /*
                     * navigateByUrl statt navigate([currentUrl]): navigate() behandelt die komplette URL
                     * als EIN Pfadsegment und kodiert einen Query-String (z.B. "?filter=...") mit ->
                     * "people-overview%3Ffilter%3D..." -> Seite nicht gefunden (PDEV-1767)
                     */
                    this.router.navigateByUrl(currentUrl);
                });

                // Letzten storage refresh markieren
                this.storageService.setLastRefreshToNow();
            },
            // onError
            () => {
                console.log('error');
            },
        );
    }

    /**
     * Marks refresh Token as used in a logout
     */
    invalidateRefreshToken() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const authServiceLogout$: Observable<any> = this.backendService.postRequest('users/invalidateRefreshToken', {
            refreshToken: this.tokenStorageService.refreshToken,
        });

        authServiceLogout$.pipe(takeUntil(this.#componentDestroyed$)).subscribe(() => {});
    }

    /**
     * Login über ein bereits vorhandenes Token (z.B. aus dem PharmaPilot).
     * Das Token wird gespeichert und über einen leeren Loginr equest (wie bei refresh())
     * gegen eine vollständige Session eingetauscht
     *
     * Dadurch werden alle benötigten Daten geladen, ohne dass ein zweiter login nötig ist.
     * Es wird nicht automatisch weitergeleitet, damit die aufrufende Komponente selbst entscheidet, wo weitergeleitet werden soll. (aktuell fest Tourenplaner, TODO:)
     * @param {string} token - Vom PharmaPilot übergebenes Authentifizierungs-Token
     * @returns {Observable<any>} - Observable mit Ergebnis des POST-Requests
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handoffLogin(token: string): Observable<any> {
        // Übergebenes Token speichern
        this.tokenStorageService.token = token;

        /*
         * Leerer login request: Backend liefert anhand des tokens die Session
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.backendService.postRequest('users/login', {}).pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            tap((result: any) => {
                if (result['success']) {
                    // session speichern und nicht weiterleiten
                    this.onSuccessfulLogin(result, true);
                }
            }),
        );
    }

    /**
     * Login über ein bereits vorhandenes Token zur Benutzer-Übernahme (Impersonate).
     * Analog zu handoffLogin(), leitet aber nach erfolgreichem Login ganz normal
     * ins System weiter (kein skipRedirect).
     *
     * Der aufrufende Tab muss zuvor über tokenStorageService.activateImpersonationMode()
     * als Impersonate-Tab markiert worden sein, damit Token im Tab-lokalen
     * Session-Storage landen und die Admin-Session im Ursprungs-Tab erhalten bleibt.
     * @param {string} token - Vom Backend ausgestelltes Token des Zielbenutzers
     * @returns {Observable<any>} - Observable mit Ergebnis des Login-Requests
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    impersonateLogin(token: string): Observable<any> {
        // Token des Zielbenutzers speichern (im Impersonate-Tab: Session-Storage)
        this.tokenStorageService.token = token;

        /*
         * Leerer Login-Request: Backend liefert anhand des Tokens die vollständige Session
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.backendService.postRequest('users/login', {}).pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            tap((result: any) => {
                if (result['success']) {
                    // Session speichern und normal ins System weiterleiten
                    this.onSuccessfulLogin(result, false);
                }
            }),
        );
    }
}
